import { useEffect, useRef } from "react";

/**
 * Apple platforms use ⌘ where others use Ctrl. Case-insensitive: Chromium reports "macOS"
 * (userAgentData), Safari and Firefox "MacIntel" (navigator.platform).
 */
export const isMac = () =>
  /mac|iphone|ipad/i.test(
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
      navigator.platform ??
      "",
  );

export interface Shortcut {
  /** KeyboardEvent.code (physical key, so ⌥ and layouts don't change it), e.g. "KeyJ". */
  code: string;
  /** ⌘ on Apple platforms, Ctrl elsewhere. */
  mod?: boolean;
  /** Ctrl on every platform (e.g. Ctrl+` toggles the terminal on Macs too). */
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  run: () => void;
}

const KEY_LABELS: Record<string, string> = {
  Enter: "Enter",
  Backquote: "`",
  ArrowLeft: "←",
  ArrowRight: "→",
};

/** How a shortcut is written on this platform, e.g. "⌘J" or "Ctrl+J". */
export function shortcutLabel(s: Omit<Shortcut, "run">): string {
  const key = KEY_LABELS[s.code] ?? s.code.replace(/^Key|^Digit/, "");
  if (isMac()) {
    return `${s.ctrl ? "⌃" : ""}${s.alt ? "⌥" : ""}${s.shift ? "⇧" : ""}${s.mod ? "⌘" : ""}${key === "Enter" ? "↩" : key}`;
  }
  const parts = [s.mod || s.ctrl ? "Ctrl" : "", s.alt ? "Alt" : "", s.shift ? "Shift" : "", key];
  return parts.filter(Boolean).join("+");
}

function matches(event: KeyboardEvent, s: Shortcut): boolean {
  const mac = isMac();
  const wantMeta = Boolean(s.mod && mac);
  const wantCtrl = Boolean(s.ctrl || (s.mod && !mac));
  return (
    event.code === s.code &&
    event.metaKey === wantMeta &&
    event.ctrlKey === wantCtrl &&
    event.altKey === Boolean(s.alt) &&
    event.shiftKey === Boolean(s.shift)
  );
}

/**
 * App-wide keyboard shortcuts. Listens in the capture phase so they work while the editor or a
 * terminal has focus (those would otherwise take the keys), and stands down while a dialog is open.
 */
export function useShortcuts(shortcuts: Shortcut[]): void {
  const latest = useRef(shortcuts);
  latest.current = shortcuts;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (document.querySelector("dialog[open]")) return;
      const shortcut = latest.current.find((s) => matches(event, s));
      if (!shortcut) return;
      event.preventDefault();
      event.stopPropagation();
      shortcut.run();
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);
}

/** The app's shortcuts (VS Code's, where a browser allows them), for handlers and tooltips. */
export const SHORTCUTS = {
  toggleTerminal: { code: "KeyJ", mod: true },
  toggleTerminalAlt: { code: "Backquote", ctrl: true },
  toggleFiles: { code: "KeyB", mod: true },
  togglePeople: { code: "KeyB", mod: true, alt: true },
  showExplorer: { code: "KeyE", mod: true, shift: true },
  run: { code: "Enter", mod: true },
  runTests: { code: "Enter", mod: true, shift: true },
  save: { code: "KeyS", mod: true },
  quickOpen: { code: "KeyP", mod: true },
  commandPalette: { code: "KeyP", mod: true, shift: true },
  commandPaletteAlt: { code: "F1" },
  nextEditor: { code: "ArrowRight", mod: true, alt: true },
  previousEditor: { code: "ArrowLeft", mod: true, alt: true },
} as const satisfies Record<string, Omit<Shortcut, "run">>;
